/**
 * useCartDisplay — turns raw `CartItem`s into display-ready rows.
 *
 * Joins each cart item to the resolved catalogue so UI components can render
 * names and per-row subtotals without each one re-walking the shell/variant
 * tree. Items whose shell/variant can't be resolved (e.g. catalogue deleted
 * the product mid-session) fall back to an "unknown product" row so the
 * cart is never empty-looking when it isn't.
 */

import { useMemo } from 'react'
import Big from 'big.js'

import type {
  CartItem,
  CartItemId,
  ResolvedCatalogue,
  ResolvedExtra,
  ResolvedShellProduct,
  ResolvedVariant,
  SalesforceId
} from 'models/purchaseFlowRevamp'

import { pickExtraPrice, pickOptionPrice } from '../productDrawer/useDrawerPricing'

import type { CartExtraSnapshot } from '../../store/slices/cartSlice'
import { baseLineTotalOf, extraAmountOf, extraBaseAmountOf, lineTotalOf } from '../../store/lineTotal'

/** One add-on of a line, named and priced. `amount` is null when the catalogue
 *  carries no price for it — a priced 0,00 and an unpriced add-on are not the
 *  same statement. */
export interface CartAddonLine {
  label: string
  amount: Big | null
}

export interface CartDisplayRow {
  id: CartItemId
  shellProductId: SalesforceId
  shellDisplayName: string
  variantDisplayName: string | null
  optionSummary: string
  /** Every add-on and extra the line actually carries, named and priced, in the
   *  order the drawer offers them. Empty when the line is bare. */
  addonLines: CartAddonLine[]
  quantity: number
  unitPrice: Big | null
  lineTotal: Big | null
  /**
   * The line total WITHOUT the saving the catalogue quoted, or null when it carries none.
   * Rendered struck through beside `lineTotal`. Derived from the snapshot taken when
   * the line was priced, so it moves with the quantity exactly as `lineTotal` does.
   */
  baseLineTotal: Big | null
  currency: string | null
  /** True when the shell has extras flagged with `recommendedFor*` that don't
   *  include this shell — advisory badge target (seed-12). */
  hasAdvisoryExtras: boolean
}

/**
 * A display-only charge that is not a cart item — today only the key-pickup
 * travel fee (V-16). Produced from the `CartExtraSnapshot`s that carry a
 * `labelKey`; snapshots without one keep contributing to the total silently, as
 * they always have.
 */
export interface CartExtraRow {
  kind: string
  /** Namespaced i18n key — the caller translates it. */
  labelKey: string
  amount: Big
  /** The amount without the session's saving, struck beside it; null or absent when there is none. */
  baseAmount?: Big | null
  /** Units the amount covers, for a label that names them; absent for a single charge. */
  count?: number
}

interface Bundle {
  rows: CartDisplayRow[]
  /** Itemised non-product charges (V-16). Already included in `total`. */
  extraRows: CartExtraRow[]
  total: Big
  currency: string | null
}

const formatOptionSummary = (
  optionValues: Record<SalesforceId, boolean | number | string>,
  variant: ResolvedVariant | null
): string => {
  if (!variant) return ''

  let count = 0
  for (const option of variant.options) {
    const raw = optionValues[option.id]
    if (option.kind === 'BOOLEAN' && raw === true) count += 1
    else if (option.kind === 'NUMBER' && typeof raw === 'number' && raw > 0) count += 1
    else if (option.kind === 'ENUM' && typeof raw === 'string' && raw.length > 0) count += 1
  }
  return count === 0 ? '' : `${count}`
}

/**
 * Names and prices every add-on and extra selected on a line, so the summary can
 * list them instead of counting them. NUMBER kinds carry their count; an ENUM
 * shows the chosen label rather than the option's own name, which is what the
 * client picked. Anything the catalogue can no longer resolve is skipped — a
 * stale draft naming a withdrawn option should not print a blank row.
 *
 * Amounts come from the same two pickers the drawer prices with, so what the
 * card itemises always sums back to the line total it sits under.
 */
const collectAddonLines = (
  optionValues: Record<SalesforceId, boolean | number | string>,
  extraIds: SalesforceId[],
  extraQuantities: Record<SalesforceId, number> | undefined,
  variant: ResolvedVariant | null,
  extras: ResolvedExtra[]
): CartAddonLine[] => {
  const lines: CartAddonLine[] = []

  for (const option of variant?.options ?? []) {
    const raw = optionValues[option.id]
    const amount = pickOptionPrice(option, raw)
    if (option.kind === 'BOOLEAN' && raw === true) lines.push({ label: option.displayName, amount })
    else if (option.kind === 'NUMBER' && typeof raw === 'number' && raw > 0) {
      lines.push({ label: `${option.displayName} \u00d7${raw}`, amount })
    }
    else if (option.kind === 'ENUM' && typeof raw === 'string' && raw.length > 0) {
      lines.push({
        label: option.enumChoices?.find((c) => c.value === raw)?.label ?? option.displayName,
        amount,
      })
    }
  }

  for (const extraId of extraIds) {
    const extra = extras.find((e) => e.id === extraId)
    if (!extra) continue
    const count = extra.kind === 'NUMBER' ? Math.max(1, extraQuantities?.[extraId] ?? 1) : 1
    const unit = pickExtraPrice(extra)
    lines.push({
      label: count > 1 ? `${extra.displayName} \u00d7${count}` : extra.displayName,
      amount: unit ? unit.times(count) : null,
    })
  }

  return lines
}

const collectAdvisoryExtras = (
  extras: ResolvedExtra[],
  shellProductId: SalesforceId,
  selectedExtraIds: SalesforceId[]
): boolean => {
  for (const extraId of selectedExtraIds) {
    const extra = extras.find((e) => e.id === extraId)
    if (!extra) continue
    const rec = extra.recommendedForShellProductIds
    if (rec && rec.length > 0 && !rec.includes(shellProductId)) return true
  }
  return false
}

const PRICE_ZERO = new Big(0)

export function buildCartDisplay(
  items: CartItem[],
  catalogue: ResolvedCatalogue | undefined,
  extrasFromInstructions: CartExtraSnapshot[] = []
): Bundle {
  const rows: CartDisplayRow[] = []
  let totalCurrency: string | null = null
  let total = PRICE_ZERO

  for (const item of items) {
    const shell: ResolvedShellProduct | undefined = catalogue?.shellProducts.find(
      (p) => p.id === item.shellProductId
    )
    const variant: ResolvedVariant | null = shell?.variants.find(
      (v) => v.id === item.variantId
    ) ?? null

    const snapshot = item.priceSnapshot
    const unitPrice = snapshot ? new Big(snapshot.unitPrice) : null
    const lineTotal = snapshot ? lineTotalOf(snapshot, item.quantity) : null
    const baseLineTotal = snapshot ? baseLineTotalOf(snapshot, item.quantity) : null
    const currency = snapshot?.currency ?? null

    if (lineTotal && currency) {
      total = total.plus(lineTotal)
      totalCurrency = totalCurrency ?? currency
    }

    const hasAdvisoryExtras = catalogue
      ? collectAdvisoryExtras(catalogue.extras, item.shellProductId, item.extraServiceIds)
      : false

    rows.push({
      id: item.id,
      shellProductId: item.shellProductId,
      shellDisplayName: shell?.displayName ?? 'Unknown product',
      // A fixed bundle is one line; what it contains is the useful subtitle, not the bundle's own variant name.
      variantDisplayName: variant?.bundledProducts?.length
        ? variant.bundledProducts.map((member) => member.displayName).filter(Boolean).join(' · ')
        : variant?.displayName ?? null,
      optionSummary: formatOptionSummary(item.optionValues, variant),
      addonLines: collectAddonLines(
        item.optionValues ?? {},
        item.extraServiceIds ?? [],
        item.extraQuantities,
        variant,
        catalogue?.extras ?? []
      ),
      quantity: item.quantity,
      unitPrice,
      lineTotal,
      baseLineTotal,
      currency,
      hasAdvisoryExtras,
    })
  }

  // Phase E-A.7 — order-level instruction extras contribute to the total
  // line so the cart sidebar reflects "Fast delivery (+€39)" etc. live.
  // V-16 — a snapshot that names itself (`labelKey`) is ALSO itemised, so a
  // BE-derived charge the user never picked from a list still reconciles with
  // the total instead of appearing as an unexplained difference.
  const extraRows: CartExtraRow[] = []
  for (const extra of extrasFromInstructions) {
    const amount = extraAmountOf(extra)
    total = total.plus(amount)
    if (extra.labelKey) extraRows.push({ kind: extra.kind, labelKey: extra.labelKey, amount, baseAmount: extraBaseAmountOf(extra), count: extra.count })
  }

  return { rows, extraRows, total, currency: totalCurrency }
}

/** React hook with memoization. */
export function useCartDisplay(
  items: CartItem[],
  catalogue: ResolvedCatalogue | undefined,
  extrasFromInstructions: CartExtraSnapshot[] = []
): Bundle {
  return useMemo(
    () => buildCartDisplay(items, catalogue, extrasFromInstructions),
    [items, catalogue, extrasFromInstructions]
  )
}
